/**
 * The one list of sketch pads in the app.
 *
 * Every trade draws on the same core; this registry is what the Trades page,
 * the Jobs roll-up, search and the storage meter all read so a new sketch pad
 * only ever has to be added in one place.
 */
import {
  ductSketchAdapter,
  electricalSketchAdapter,
  structuralSketchAdapter,
} from "./adapters";
import { mergeLines, runLength, type SavedSketch, type TradeSketchAdapter } from "./index";
import { autoKeyFor, loadSaves, readJson } from "./storage";

export interface SketchToolEntry {
  id: string;
  /** what a worker calls it */
  label: string;
  /** trade id used by the Trades hub */
  trade: string;
  /** where it opens */
  route: string;
  /** localStorage root; `-saves` / `-autosave` hang off it */
  storageKey: string;
  /** the drawing engine, when the pad runs on the shared core */
  adapter?: TradeSketchAdapter;
}

export const SKETCH_TOOLS: SketchToolEntry[] = [
  {
    id: "pipe",
    label: "Pipe Sketch",
    trade: "pipe",
    route: "/sketch",
    storageKey: "tt-pipe-sketch",
  },
  {
    id: "electrical",
    label: "Conduit Sketch",
    trade: "electrical",
    route: "/electrical?tool=sketch",
    storageKey: electricalSketchAdapter.storageKey,
    adapter: electricalSketchAdapter,
  },
  {
    id: "duct",
    label: "Duct Sketch",
    trade: "hvac",
    route: "/ductwork?tool=sketch",
    storageKey: ductSketchAdapter.storageKey,
    adapter: ductSketchAdapter,
  },
  {
    id: "structural",
    label: "Structural Sketch",
    trade: "structural",
    route: "/structural?tool=sketch",
    storageKey: structuralSketchAdapter.storageKey,
    adapter: structuralSketchAdapter,
  },
];

/** Every storage key the sketch pads own — used by the storage meter/backup. */
export const SKETCH_STORAGE_KEYS = SKETCH_TOOLS.flatMap((t) => [
  `${t.storageKey}-saves`,
  autoKeyFor(t.storageKey),
]);

export interface SketchRollup {
  id: string;
  label: string;
  route: string;
  /** saved drawings (for this job when a job is given) */
  count: number;
  feet: number;
  material: number;
  hours: number;
  /** false for Pipe Sketch, which prices through its own cut list */
  priced: boolean;
}

/**
 * What every trade has drawn, ready for a job card. Reads device storage only.
 */
export function readSketchRollups(jobId?: string): SketchRollup[] {
  return SKETCH_TOOLS.map((tool) => {
    const saves: SavedSketch[] = loadSaves(tool.storageKey).filter(
      (s) => !jobId || s.jobId === jobId,
    );

    let feet = 0;
    let material = 0;
    let hours = 0;

    if (tool.adapter) {
      for (const save of saves) {
        const doc = save.doc ?? { runs: [], markers: [] };
        feet += (doc.runs ?? []).reduce((sum, r) => sum + runLength(r), 0);
        for (const line of mergeLines(tool.adapter.takeoff(doc))) {
          material += line.qty * line.unitCost;
          hours += line.hours;
        }
      }
    } else {
      for (const save of saves) {
        const sum = (save as unknown as { summary?: { feet?: number } }).summary;
        feet += sum?.feet ?? 0;
      }
    }

    return {
      id: tool.id,
      label: tool.label,
      route: tool.route,
      count: saves.length,
      feet,
      material,
      hours,
      priced: Boolean(tool.adapter),
    };
  });
}

/** Anything drawn at all? Keeps the job card out of the way when empty. */
export const hasAnySketch = (rollups: SketchRollup[]) =>
  rollups.some((r) => r.count > 0);

export function readSketchAutosaveName(storageKey: string): string | null {
  const auto = readJson<{ name?: string }>(autoKeyFor(storageKey), {});
  return auto?.name ?? null;
}
